import { Link, useLocation, useParams } from 'react-router-dom';
import { ETAPES, ETAPE_INFO, listeEt } from '@dinertournant/core';
import { BandeauSignature, IconeAgenda, IconeCoche, PilluleEtape } from '../../components/index.js';
import { capitale, dateCourte, heureCourte, type EvenementPublic, type FoyerPublic } from '../../lib/api.js';

interface Etat {
  token?: string;
  foyer: FoyerPublic;
  event: EvenementPublic;
  mode: 'inscription' | 'modification';
}

function prenoms(membres: string[]): string {
  return listeEt(membres.map((m) => m.split(/\s+/)[0] ?? m));
}

function lienAgenda(event: EvenementPublic): string {
  const jour = event.date.replace(/-/g, '');
  const h = (x: string) => x.replace(/:/g, '').slice(0, 4) + '00';
  const fin = String(Number(event.horaires.dessert.slice(0, 2)) + 2).padStart(2, '0') + event.horaires.dessert.slice(3, 5) + '00';
  const ics = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Diner tournant//FR', 'BEGIN:VEVENT', `DTSTART:${jour}T${h(event.horaires.entree)}`, `DTEND:${jour}T${fin}`, `SUMMARY:${event.nom}`, 'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

/** Écran 2b : confirmation récapitulative. */
export function Confirmation() {
  const { state } = useLocation() as { state: Etat | null };
  const { slug, token } = useParams<{ slug: string; token: string }>();
  if (!state) {
    return (
      <div className="page-mobile">
        <BandeauSignature />
        <div style={{ padding: '36px 20px' }} className="pile">
          <h1 className="display">Rien à afficher</h1>
          <p className="texte-doux">{slug ? <Link to={`/i/${slug}`}>Retour au formulaire d'inscription</Link> : 'Reprenez le lien reçu par e-mail.'}</p>
        </div>
      </div>
    );
  }
  const { foyer, event, mode } = state;
  const jetonModif = state.token ?? token;
  const nomCourt = foyer.nom.replace(/^les\s+/i, '');
  const emails = [...new Set([...(foyer.emails ?? []), foyer.email].filter((e): e is string => !!e))];
  const dispos = ETAPES.filter((e) => foyer.disponibilites.includes(e));
  const parcoursVers = (() => {
    const [y, m, d] = event.dateLimiteInscription.split('-').map(Number) as [number, number, number];
    // Le parcours est annoncé 2 jours après la fin des inscriptions.
    const dt = new Date(y, m - 1, d + 2);
    return dateCourte(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`);
  })();

  return (
    <div className="page-mobile">
      <BandeauSignature />
      <div style={{ padding: '36px 20px 0', gap: 20 }} className="pile">
        <div className="rond-ok" aria-hidden="true">
          <IconeCoche taille={30} />
        </div>
        <div className="pile" style={{ gap: 10 }}>
          <h1 className="display" style={{ fontSize: 32, lineHeight: 1.1 }}>
            {mode === 'inscription' ? `C'est noté, ${prenoms(foyer.membres)} !` : `C'est mis à jour, ${prenoms(foyer.membres)} !`}
          </h1>
          <p className="texte-doux">
            Votre inscription au {event.nom.replace(/^Dîner tournant/i, 'dîner tournant')} est {mode === 'inscription' ? 'enregistrée' : 'modifiée'}.
            {mode === 'inscription' && emails.length ? ` Un e-mail de confirmation vient de partir vers ${listeEt(emails)}.` : ''}
          </p>
        </div>

        <section className="recap" aria-labelledby="recap">
          <h2 id="recap">Récapitulatif</h2>
          <div className="row">
            <span className="row-k">Voiture</span>
            <span className="row-v">
              {nomCourt} · {foyer.membres.length} personne{foyer.membres.length > 1 ? 's' : ''}
              {foyer.membres.length > 1 ? (
                <>
                  <br />
                  <span className="sous">{prenoms(foyer.membres)}</span>
                </>
              ) : null}
            </span>
          </div>
          <div className="row">
            <span className="row-k">Adresse</span>
            <span className="row-v">
              {foyer.adresse}
              {foyer.complementAdresse ? (
                <>
                  <br />
                  <span className="sous">{foyer.complementAdresse}</span>
                </>
              ) : null}
            </span>
          </div>
          <div className="row">
            <span className="row-k">Contact</span>
            <span className="row-v">
              {emails.map((e, i) => (
                <span key={e}>
                  {i > 0 ? <br /> : null}
                  {e}
                </span>
              ))}
              {foyer.telephone ? (
                <>
                  <br />
                  {foyer.telephone}
                </>
              ) : null}
            </span>
          </div>
          <div className="row">
            <span className="row-k">À table</span>
            <span className="row-v">{[...foyer.regimes.filter((r) => r !== 'Allergie'), foyer.precisionsRegime].filter(Boolean).join(' · ') || 'Aucune indication'}</span>
          </div>
          <div className="row" style={{ flexDirection: 'column', gap: 10 }}>
            <span style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
              <span className="row-k">Vous recevez</span>
              <span className="row-v">
                {foyer.veutRecevoir && dispos.length ? 'Oui, une seule fois' : 'Non, cette fois'}
                {foyer.veutRecevoir && foyer.capaciteMax ? (
                  <>
                    <br />
                    <span className="sous">jusqu'à {foyer.capaciteMax} personnes</span>
                  </>
                ) : null}
              </span>
            </span>
            {foyer.veutRecevoir && dispos.length ? (
              <span style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                {dispos.map((e, i) => (
                  <span key={e} style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                    {i > 0 ? <span style={{ fontSize: 13, fontWeight: 600 }} className="texte-muet">ou</span> : null}
                    <PilluleEtape etape={e} />
                  </span>
                ))}
              </span>
            ) : null}
          </div>
        </section>

        <section className="pile" aria-labelledby="suite">
          <h2 id="suite" style={{ fontSize: 18, fontWeight: 700 }}>
            Et maintenant ?
          </h2>
          <div className="chrono">
            <div className="rail">
              <span className="point plein" />
              <span className="trait" />
            </div>
            <div className="texte">
              <div className="titre">{dateCourte(event.dateLimiteInscription)} · fin des inscriptions</div>
              <div className="detail">L'organisateur constitue les tables pour que chacun rencontre un maximum de nouvelles têtes.</div>
            </div>
          </div>
          <div className="chrono">
            <div className="rail">
              <span className="point" />
              <span className="trait" />
            </div>
            <div className="texte">
              <div className="titre">Vers le {parcoursVers} · votre parcours</div>
              <div className="detail">Vous saurez quelle étape vous recevez, combien de convives attendre et leurs contraintes alimentaires.</div>
            </div>
          </div>
          <div className="chrono">
            <div className="rail">
              <span className="point" />
            </div>
            <div className="texte" style={{ paddingBottom: 0 }}>
              <div className="titre">
                {capitale(dateCourte(event.date))} · {heureCourte(event.horaires.entree)}
              </div>
              <div className="detail">Bonne soirée ! {ETAPE_INFO.entree.label} {heureCourte(event.horaires.entree)}, {ETAPE_INFO.plat.label.toLowerCase()} {heureCourte(event.horaires.plat)}, {ETAPE_INFO.dessert.label.toLowerCase()} {heureCourte(event.horaires.dessert)}.</div>
            </div>
          </div>
        </section>

        <div className="pile" style={{ gap: 10 }}>
          <a className="btn principal large" href={lienAgenda(event)} download={`${event.nom}.ics`}>
            <IconeAgenda /> Ajouter à mon agenda
          </a>
          {jetonModif ? (
            <Link className="btn large" to={`/f/${jetonModif}`}>
              Modifier mon inscription
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}
